import { OptionType } from '@evinced-private/ui-common';

import { IWebSdkTest } from 'src/apps/web-sdks/types/WebSdkDashboardServiceTypes';
import { apiCall } from 'src/common/services/api/ServerApi';
import { Logger } from 'src/common/services/Logger';
import { ConfigurationLabel } from 'src/common/types/ConfigurationTypes';

import { addChanges, buildBasePayload } from './builders/PayloadBuilder';
import { buildAllRowChanges } from './helpers/ChangeBuildingHelpers';
import {
	createAdditionFromChange,
	findRowsWithLabel,
	LabelChangeForUpdate,
	parseLabelChange,
	validateLabelChange
} from './helpers/LabelChangeHelpers';
import { buildPayloadsForRows } from './helpers/PayloadBuildingHelpers';
import {
	transformAdditionsForRow,
	transformRemovalsForRow
} from './transformers/LabelTransformers';
import { BatchUpdatePayload, RequestPayload } from './types/PayloadTypes';

const API_ENDPOINT = `${import.meta.env.VITE_API_ENDPOINT}/update/wsdk/batch`;

const buildUpdatePayloads = (
	testRows: IWebSdkTest[],
	rowIdToAdditionsMap: Map<string, Array<{ labelType: OptionType; value: OptionType }>>,
	configuredLabels?: ConfigurationLabel[]
): RequestPayload[] => {
	return buildPayloadsForRows(testRows, rowIdToAdditionsMap, (row, rowAdditions) =>
		buildAllRowChanges(row, rowAdditions, configuredLabels)
	);
};

export const addLabels = async (
	testRows: IWebSdkTest[],
	additions: Array<{ labelType: OptionType; value: OptionType }>,
	configuredLabels?: ConfigurationLabel[]
): Promise<void> => {
	try {
		if (!testRows.length || !additions.length) {
			return;
		}

		const updates = testRows
			.map((row: IWebSdkTest) => {
				const runId = row?.runId;
				if (!runId) {
					return null;
				}

				const changes = transformAdditionsForRow(row, additions, configuredLabels);
				if (!changes.length) {
					return null;
				}

				const payload = buildBasePayload(runId);
				return addChanges(payload, changes);
			})
			.filter((payload): payload is RequestPayload => payload !== null);

		if (!updates.length) {
			return;
		}

		await apiCall('POST', API_ENDPOINT, {
			data: { updates },
			isAbsoluteUrl: true
		});
	} catch (error) {
		Logger.error('Error adding labels to tests:', error);
		throw error;
	}
};

export const removeLabels = async (testRows: IWebSdkTest[], removals: string[]): Promise<void> => {
	try {
		if (!testRows.length || !removals.length) {
			return;
		}

		const updates = testRows
			.map((row: IWebSdkTest) => {
				const runId = row?.runId;
				if (!runId) {
					return null;
				}

				// Check if this row has any of the labels to remove
				const rowLabels = row.labels ?? [];
				const hasRemoval = removals.some((removalString) => rowLabels.includes(removalString));
				if (!hasRemoval) {
					return null;
				}

				// Transform removals for this specific row (checking environments/flows)
				const changes = transformRemovalsForRow(removals, row);
				if (!changes.length) {
					return null;
				}

				const payload = buildBasePayload(runId);
				return addChanges(payload, changes);
			})
			.filter((payload): payload is RequestPayload => payload !== null);

		if (!updates.length) {
			return;
		}

		const batchPayload: BatchUpdatePayload = { updates };

		await apiCall('POST', API_ENDPOINT, {
			data: batchPayload,
			isAbsoluteUrl: true
		});
	} catch (error) {
		Logger.error('Error removing labels from tests:', error);
		throw error;
	}
};

const buildRowIdToAdditionsMap = (
	testRows: IWebSdkTest[],
	changes: LabelChangeForUpdate[],
	configuredLabels?: ConfigurationLabel[]
): Map<string, Array<{ labelType: OptionType; value: OptionType }>> => {
	const rowIdToAdditionsMap = new Map<
		string,
		Array<{ labelType: OptionType; value: OptionType }>
	>();

	for (const change of changes) {
		if (!validateLabelChange(change, configuredLabels)) {
			continue;
		}

		const parsed = parseLabelChange(change, configuredLabels);
		if (!parsed) {
			continue;
		}

		const rowIds = findRowsWithLabel(testRows, change.oldLabelValue, configuredLabels);
		if (rowIds.size === 0) {
			continue;
		}

		const addition = createAdditionFromChange(parsed.oldParsed, parsed.newParsed);

		for (const rowId of rowIds) {
			if (!rowIdToAdditionsMap.has(rowId)) {
				rowIdToAdditionsMap.set(rowId, []);
			}
			const additions = rowIdToAdditionsMap.get(rowId);
			if (additions) {
				additions.push(addition);
			}
		}
	}

	return rowIdToAdditionsMap;
};

export const updateLabels = async (
	testRows: IWebSdkTest[],
	changes: LabelChangeForUpdate[],
	configuredLabels?: ConfigurationLabel[]
): Promise<void> => {
	try {
		if (!testRows.length || !changes.length) {
			return;
		}

		const rowIdToAdditionsMap = buildRowIdToAdditionsMap(testRows, changes, configuredLabels);

		if (rowIdToAdditionsMap.size === 0) {
			return;
		}

		const updates = buildUpdatePayloads(testRows, rowIdToAdditionsMap, configuredLabels);

		if (updates.length === 0) {
			return;
		}

		const batchPayload: BatchUpdatePayload = { updates };

		await apiCall('POST', API_ENDPOINT, {
			data: batchPayload,
			isAbsoluteUrl: true
		});
	} catch (error) {
		Logger.error('Error updating labels on tests:', error);
		throw error;
	}
};
